import {useLayoutEffect, useRef} from 'react'
import {useLocation} from '@github-ui/react-router'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {PULL_REQUEST_EVENTS} from '@github-ui/timeline-items/Events'

/**
 * Deep-links to a PR conversation timeline anchor (e.g. `#issuecomment-123`) rely on
 * the target element existing in the DOM at initial paint so the browser can scroll to
 * and focus it. Because the Timeline is fetched as a deferred `RailsPartial`, the target
 * doesn't exist at initial paint anymore, so we recreate that behavior once the Timeline
 * has loaded:
 *
 * 1. While the Timeline is loading, move focus to its skeleton so keyboard and
 *    screen-reader users land somewhere meaningful instead of `<body>`.
 * 2. Once the Timeline finishes loading, focus (and scroll to) the element matching the
 *    URL hash — but only if the user hasn't moved focus away in the meantime.
 *
 * No-ops entirely when the hash doesn't match a known PR timeline anchor prefix (e.g.
 * `#top`, the sticky title's "back to top" link, has no backing element at all and
 * would otherwise hijack focus/scroll while we wait forever for a target that never
 * appears), when the hash's target already exists at mount (the browser already handled
 * it, e.g. the Timeline happened to be cached), or when there's no hash to begin with.
 *
 * This route component can stay mounted across soft navigations (e.g. clicking a link to
 * a different PR, or a hash-only deep link changing while the Timeline is still loading),
 * so the pending target is recomputed whenever `location.pathname`/`location.hash` change
 * rather than only once for the lifetime of the component.
 */
export function useFocusDeferredTimelineHash(
  isTimelineLoading: boolean,
  skeletonRef: React.RefObject<HTMLElement | null>,
): void {
  const location = useLocation()
  const pendingHashRef = useRef<string | null | undefined>(undefined)
  const hasFocusedSkeletonRef = useRef(false)
  // The `pathname`/`hash` this hook last computed `pendingHashRef` for.
  const processedLocationKeyRef = useRef<string | undefined>(undefined)

  useLayoutEffect(() => {
    if (!isFeatureEnabled('prs_defer_timeline')) return

    const locationKey = `${location.pathname}${location.hash}`
    if (processedLocationKeyRef.current !== locationKey) {
      processedLocationKeyRef.current = locationKey
      hasFocusedSkeletonRef.current = false
      const hash = location.hash.slice(1)
      // Only treat the hash as a pending Timeline target when it matches a known
      // anchor prefix. Otherwise (e.g. the hash targets something outside the
      // Timeline, or the Timeline loaded before this effect ran), leave it alone —
      // either the browser already handled it, or it was never a real target.
      pendingHashRef.current =
        hash && isPullRequestTimelineHash(location.hash) && !document.getElementById(hash) ? hash : null
    }

    const hash = pendingHashRef.current
    if (!hash) return

    const active = document.activeElement
    const focusUnclaimed = active === null || active === document.body || active === skeletonRef.current

    if (isTimelineLoading) {
      if (!hasFocusedSkeletonRef.current && focusUnclaimed) {
        hasFocusedSkeletonRef.current = true
        skeletonRef.current?.focus({preventScroll: true})
        scrollIntoViewNextFrame(skeletonRef.current)
      }
      return
    }

    // The Timeline has finished loading (or was already loaded on the first run for
    // this location) — only attempt to resolve the target once per location.
    pendingHashRef.current = null
    if (!focusUnclaimed) return

    const target = document.getElementById(hash)
    if (!target) return

    focusElement(target)
  }, [isTimelineLoading, skeletonRef, location.pathname, location.hash])
}

/**
 * Whether `hash` (including its leading `#`) matches one of the known PR timeline
 * anchor prefixes: comments, reviews, review comments, and generic events (see
 * `CommentsHelper#comment_dom_id` and `app/views/issues/events/_simple_events.html.erb`
 * in github/github for the corresponding server-side `id` conventions). Anchors that
 * live elsewhere on the page — e.g. `#top`, the sticky title's "back to top" link, which
 * has no backing element at all — never match, so we don't hijack focus/scroll waiting
 * for a target that will never appear.
 */
function isPullRequestTimelineHash(hash: string): boolean {
  return PULL_REQUEST_EVENTS.some(prefix => hash.startsWith(prefix))
}

/**
 * `<ScrollRestoration>` (rendered after the routed content, see
 * `RoutesWithProviders.tsx`'s `sharedInternals`) runs its own layout effect on mount that
 * falls back to scrolling to the top of the page whenever `location.hash` doesn't
 * resolve to an element yet — which is always true here on first mount, since the
 * Timeline hasn't loaded. That effect runs *after* this hook's in the same commit, so a
 * synchronous `scrollIntoView` call here would be immediately overwritten. Deferring to
 * the next animation frame lets ours run after `<ScrollRestoration>` has already settled.
 */
function scrollIntoViewNextFrame(element: HTMLElement | null): void {
  if (!element) return
  requestAnimationFrame(() => {
    element.scrollIntoView({block: 'start'})
  })
}

async function focusElement(element: HTMLElement): Promise<void> {
  const link = element.querySelector<HTMLElement>(`[href='#${element.id}']`)

  scrollIntoViewNextFrame(element)
  if (link) {
    const oldValue = link.getAttribute('data-turbo')
    link.setAttribute('data-turbo', 'false')
    setTimeout(() => {
      link.click()
    }, 0)
    if (oldValue === null) {
      link.removeAttribute('data-turbo')
    } else {
      link.setAttribute('data-turbo', oldValue)
    }
  }
}
